import React, { useId, useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons'
import { faSmile as faSmileRegular } from '@fortawesome/free-regular-svg-icons'
import { faSmile as faSmileSolid } from '@fortawesome/free-solid-svg-icons'

import 'emoji-picker-element'

import { openPortalModal } from 'src/widgets/embed/openPortalModal'
import { injectLocalPortalModal } from './injectLocalPortalModal'
import useValidation from './useValidation'

const MAX_LENGTH = 150

// Donor comment composer used both pre-donation (in DonationExtras)
// and post-donation (on ThankYou as a fallback for donors who didn't
// comment during the flow). Universal: the consumer hands in a
// committed `value` + `submitted` flag plus an `onSave` callback;
// pre-donation it's just local state via useComment, post-donation
// it's a fetch to /donations/{token}/comment.
//
// Pattern matches Gift Aid (link → modal → confirmed state) with
// added Edit + Remove links in the confirmed state (both suppressed
// via editable=false on the post-donation flow per design — comments
// can't be edited or removed after publication; the email
// notification has already gone out by then).
//
// The dialog rides openPortalModal so that in properly-embedded mode
// the composer pops at host-page level instead of getting buried
// inside the donate widget iframe — which used to mean the donor
// had to scroll the partner page to see it once the emoji picker
// expanded the dialog past the iframe's visible area. Mode 1/3 falls
// back to the local TW4 renderer (`<dialog class="widget-modal">`).

const Comment = ({
  i18n,
  id: providedId,
  value,
  submitted,
  onSave,
  onRemove,
  editable = true,
  blank_modal_src,
  emoji_picker_loader_src,
  widget_instance_token,
}) => {
  // Panel.Collection co-renders the active and transitioning panels
  // for ~330ms during step transitions, which means Contact's and
  // ExpressCheckout's DonationExtras (and their Comment composers)
  // briefly coexist in the React tree. useId disambiguates the modal
  // id so the openPortalModal cache doesn't merge them. The post-
  // donation usage on ThankYou passes an explicit `providedId` (no
  // Panel collision there) for grep-ability.
  const generatedId = useId()
  const localId = providedId || `comment-${generatedId}`
  // Widget-instance-token suffix mirrors the picker + gift-aid
  // pattern — two donate widgets on the same page each get their own
  // cached modal iframe in mode 2 (otherwise the second widget's
  // openPortalModal would hit the first's cached iframe and never
  // receive its own portalReady).
  const modalId = `${localId}:${widget_instance_token}`

  const [ portal, setPortal ] = useState(null)
  const [ text, setText ] = useState(value)
  const [ pickerOpen, setPickerOpen ] = useState(false)
  const [ saving, setSaving ] = useState(false)
  // Two-click confirmation for the destructive Remove action.
  // Native confirm() can be silently blocked when the widget is in a
  // non-sandboxed iframe (Chrome's defaults), so we roll our own:
  // first click flips the link into a "Click to confirm" state in
  // danger color, second click within 3s commits, otherwise it
  // auto-reverts. handleOpen clears it too in case the donor
  // changes their mind by going to Edit instead.
  const [ confirmingRemove, setConfirmingRemove ] = useState(false)

  // Use the shared declarative validation pipeline so error messages
  // come pre-localized via the existing donation_errors locale block
  // (`blank`, `too_long`). validate() runs the rules + sets errors
  // state; resetErrorsFor clears them as the donor edits. Network
  // errors from onSave (post-donation flow) get pushed in via
  // addError so they read out of the same surface.
  const validation = useValidation({
    i18n,
    fieldSet: { comment: { required: true, maxLength: MAX_LENGTH } },
  })
  const error = validation.errors.comment?.[0]

  const handleOpen = async () => {
    setText(value)
    setPickerOpen(false)
    setConfirmingRemove(false)
    validation.setErrors({})
    const result = await openPortalModal({
      id: modalId,
      src: blank_modal_src,
      ariaLabel: i18n.t('comment.heading'),
      maxWidth: '30rem',
      renderLocal: injectLocalPortalModal,
    })
    setPortal(result)
  }

  // Auto-revert the remove-confirmation state after 3 seconds so a
  // stale "Click to confirm" doesn't sit forever waiting for a
  // second click that never comes.
  useEffect(() => {
    if (!confirmingRemove) return
    const timer = setTimeout(() => setConfirmingRemove(false), 3000)
    return () => clearTimeout(timer)
  }, [ confirmingRemove ])

  // Make sure <emoji-picker> is registered in the modal iframe's
  // customElements registry in mode 2. The donate widget already
  // imports emoji-picker-element at module load (which registers the
  // tag against the donate widget's realm — fine for mode 1/3 where
  // the local renderer mounts into the donate widget's document).
  // For mode 2 the React portal mounts the tag into a same-origin
  // sibling iframe whose registry is empty, and cross-realm class
  // registration fails at element-construction time (Chrome throws
  // "createElement: result must be in the same document" — the
  // class's HTMLElement prototype is from the wrong realm). Solve it
  // by injecting a script tag whose module evaluates inside the
  // iframe; the import statement there registers the custom element
  // in the iframe's own realm. Idempotent: skip when already
  // registered (cached iframe on second open). Modes 1/3 are a
  // no-op (targetDoc === document, registry already populated).
  useEffect(() => {
    if (!portal?.target) return
    const targetDoc = portal.target.ownerDocument
    if (targetDoc === document) return
    const targetWindow = targetDoc.defaultView
    if (targetWindow?.customElements?.get('emoji-picker')) return
    const script = targetDoc.createElement('script')
    script.src = emoji_picker_loader_src
    targetDoc.head.appendChild(script)
  }, [ portal, emoji_picker_loader_src ])

  // <emoji-picker> is a custom element; React doesn't bind to its
  // events. Subscribe via a ref-callback (refs from React-rendered
  // elements in a portal'd subtree work the same as in-tree refs).
  // Functional setState on insert so we read the latest typed text
  // rather than capturing stale closure state from when the picker
  // opened.
  const [ pickerEl, setPickerEl ] = useState(null)
  const [ textareaEl, setTextareaEl ] = useState(null)
  useEffect(() => {
    if (!pickerEl) return
    const handleEmoji = e => {
      const emoji = e.detail.unicode
      const start = textareaEl?.selectionStart ?? text.length
      const end = textareaEl?.selectionEnd ?? text.length
      setText(prev => prev.slice(0, start) + emoji + prev.slice(end))
      validation.resetErrorsFor('comment')
      // Restore caret after React rerenders.
      requestAnimationFrame(() => {
        textareaEl?.focus()
        const pos = start + emoji.length
        textareaEl?.setSelectionRange(pos, pos)
      })
    }
    pickerEl.addEventListener('emoji-click', handleEmoji)
    return () => pickerEl.removeEventListener('emoji-click', handleEmoji)
  }, [ pickerEl, textareaEl ])

  const overLimit = text.length > MAX_LENGTH

  // Validate at submit time rather than blocking input. The textarea
  // accepts overflow and the count flips red so the donor can see
  // and trim — typing-prevention on a textarea is jarring UX,
  // especially when editing existing content.
  const handleSave = async () => {
    if (saving) return
    if (!validation.validate({ comment: text })) return
    setSaving(true)
    try {
      await onSave(text.trim())
      portal?.close()
    } catch (e) {
      validation.addError('comment', e?.message || i18n.t('ajax_error'))
    } finally {
      setSaving(false)
    }
  }

  const handleRemove = () => {
    if (!confirmingRemove) {
      setConfirmingRemove(true)
      return
    }
    setConfirmingRemove(false)
    onRemove?.()
  }

  return (
    <div>
      {submitted ? (
        <p className="text-success inline-flex flex-wrap items-center gap-x-2">
          <span className="inline-flex items-center gap-2">
            <FontAwesomeIcon icon={faCheckCircle} />
            {i18n.t('comment.added')}
          </span>
          {editable && (
            <>
              <span aria-hidden="true" className="text-gray-400">·</span>
              <button
                type="button"
                onClick={handleOpen}
                className="btn-link underline underline-offset-2 text-sm"
              >
                {i18n.t('comment.action_edit')}
              </button>
              {onRemove && (
                <>
                  <span aria-hidden="true" className="text-gray-400">·</span>
                  <button
                    type="button"
                    onClick={handleRemove}
                    className={`btn-link underline underline-offset-2 text-sm ${
                      confirmingRemove
                        ? 'text-danger font-semibold'
                        : 'text-gray-500'
                    }`}
                  >
                    {confirmingRemove
                      ? i18n.t('comment.action_remove_confirm')
                      : i18n.t('comment.action_remove')}
                  </button>
                </>
              )}
            </>
          )}
        </p>
      ) : (
        <button
          type="button"
          onClick={handleOpen}
          className="btn-link underline underline-offset-2"
        >
          {i18n.t('comment.action_add')}
        </button>
      )}

      {portal?.target && createPortal(
        <>
          <button
            type="button"
            className="widget-modal-close hide-in-blank"
            aria-label={i18n.t('close')}
            onClick={() => portal.close()}
          />
          <div className="px-3 py-4 chrome:px-4 chrome:py-5 space-y-3">
            <h2 className="text-xl font-semibold">{i18n.t('comment.heading')}</h2>
            <div>
              <textarea
                ref={setTextareaEl}
                value={text}
                onChange={e => {
                  setText(e.target.value)
                  validation.resetErrorsFor('comment')
                }}
                rows={3}
                aria-invalid={error || overLimit ? 'true' : undefined}
                className="form-control w-full"
                placeholder={i18n.t('comment.placeholder')}
                autoFocus
              />
              {error && (
                <p role="alert" className="mt-1 text-sm text-danger">{error}</p>
              )}
            </div>
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => setPickerOpen(o => !o)}
                aria-label={i18n.t('comment.action_emoji_picker')}
                aria-expanded={pickerOpen}
                className="btn-link"
              >
                <FontAwesomeIcon
                  icon={pickerOpen ? faSmileSolid : faSmileRegular}
                  className="fa-lg"
                />
              </button>
              <span className={`text-sm ${overLimit ? 'text-danger font-semibold' : 'text-gray-500'}`}>
                {text.length}/{MAX_LENGTH}
              </span>
            </div>
            {/* Inline expandable emoji picker — sits inside the
                comment dialog rather than a nested modal so the
                top-layer stack stays single-deep. <emoji-picker> is
                full-width here; the host element's own
                shadow-DOM handles its internal layout. */}
            {pickerOpen && (
              <div className="overflow-hidden -mx-2 -mt-3">
                <div className="-m-px">
                  <emoji-picker
                    ref={setPickerEl}
                    class="light w-full block"
                  />
                </div>
              </div>
            )}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => portal.close()}
                disabled={saving}
                className="btn btn-outline-primary flex-1"
              >
                {i18n.t('cancel')}
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="btn btn-primary flex-1"
              >
                {saving ? i18n.t('submitting') : i18n.t('comment.save')}
              </button>
            </div>
          </div>
        </>,
        portal.target
      )}
    </div>
  )
}

export default Comment
